import React from 'react';
import { Image, Linking, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Colors } from '../../constants/Colors';
import { Images } from '../../constants/Images';
import { AppRoutes } from '../../navigation/Routes.types';
import { useAuthStore } from '../../store/AuthStore';
import { useBagStore } from '../../store/BagStore';
import { useFilterStore } from '../../store/FilterStore';
import { useLimitStore } from '../../store/LimitStore';
import { useUserStore } from '../../store/UserStore';
import useExtendedTermsStore from '../../store/ExtendedTermsStore';
import AuthService from '../../services/AuthService/AuthService';
import { styles } from './styles';
import { getAnalytics, logEvent } from '@react-native-firebase/analytics';

interface DesktopSidebarProps {
  state: any;
  descriptors: any;
  navigation: any;
}

const analyticsInstance = getAnalytics();

const DesktopSidebar = ({ state, descriptors, navigation }: DesktopSidebarProps) => {
  const { user } = useUserStore();
  const { setToken, defUser } = useAuthStore();
  const { clearCart } = useBagStore();
  const { clearFilters } = useFilterStore();
  const { limit, balance } = useLimitStore();
  const { clearData: clearExtendedTerms } = useExtendedTermsStore();
  const totalItems = useBagStore(s => s.getTotalItems());
  const storeAccess = !!user?.storeAccess;

  const handleLogOff = () => {
    AuthService.logoutPost().catch(error => console.error(error));
    setToken('');
    defUser(null);
    clearCart();
    clearFilters();
    clearExtendedTerms();
  };

  const navigateTo = (route: string) => {
    navigation.navigate(route);
  };

  const navigateToStack = (route: string) => {
    const parentNavigation = navigation.getParent();
    if (parentNavigation) {
      parentNavigation.navigate(route);
    } else {
      navigation.navigate(route);
    }
  };

  const primaryNavItems = [
    {
      label: 'Home',
      route: 'Home',
      icon: Images.homeIcon,
      activeIcon: Images.houseP,
    },
    ...(storeAccess
      ? [
          {
            label: 'Sacola',
            route: 'Sacola',
            icon: Images.bag,
            activeIcon: Images.bag,
            showBadge: true,
          },
        ]
      : [
          {
            label: 'Equipamentos',
            route: 'Equipamentos',
            icon: Images.tool,
            activeIcon: Images.tool,
          },
        ]),
    {
      label: 'Meus Pedidos',
      route: 'Meus pedidos',
      icon: Images.cart,
      activeIcon: Images.cart,
    },
  ];

  const menuItems = [
    {
      label: 'Pedidos Programados',
      icon: Images.documentIcon,
      onPress: () => {
        navigateToStack(AppRoutes.SCHEDULED_ORDER_MENU);
      },
    },
    {
      label: 'Empresas',
      icon: Images.fabric,
      onPress: () => {
        logEvent(analyticsInstance, 'companies_click', {
          group_id: user?.current_client?.group_id,
          client_code: user?.clientCode,
        });
        navigation.navigate('Home', { screen: 'HomeScreen', params: { companyModal: true } });
      },
    },
    {
      label: 'Meus Débitos',
      icon: Images.documentIcon,
      onPress: () => {
        logEvent(analyticsInstance, 'debts_click', { client_code: user?.clientCode });
        navigateToStack(AppRoutes.DEBTS);
      },
    },
    {
      label: 'Meus Cartões',
      icon: Images.card,
      onPress: () => navigateToStack(AppRoutes.MY_CARDS),
    },
    {
      label: 'Dúvidas Frequentes',
      icon: Images.doubt,
      onPress: () => {
        logEvent(analyticsInstance, 'faq_click', { client_code: user?.clientCode });
        navigateToStack(AppRoutes.SUPPORT);
      },
    },
    {
      label: 'Equipamentos',
      icon: Images.tool,
      onPress: () => {
        logEvent(analyticsInstance, 'equipments_click', { client_code: user?.clientCode });
        navigateToStack(AppRoutes.EQUIPAMENT);
      },
    },
    {
      label: 'Pedidos de Troca',
      icon: Images.exchangeIcon,
      onPress: () => {
        logEvent(analyticsInstance, 'exchanges_click', { client_code: user?.clientCode });
        navigateToStack(AppRoutes.EXCHANGES);
      },
    },
  ];

  const secondaryItems = [
    {
      label: 'Configuração',
      icon: Images.gear,
      onPress: () => {
        logEvent(analyticsInstance, 'settings_click', { client_code: user?.clientCode });
        navigateToStack(AppRoutes.PROFILE);
      },
    },
    {
      label: 'Suporte WhatsApp',
      icon: Images.whatsapp,
      onPress: () => {
        logEvent(analyticsInstance, 'support_phone_click', { client_code: user?.clientCode });
        Linking.openURL('https://wa.me/+556199763531');
      },
    },
  ];

  const activeTabIndex = state.index;
  const activeRouteName = state.routes[activeTabIndex]?.name;

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {/* Logo */}
        <View style={styles.logoContainer}>
          <Image source={Images.logoRefri} style={styles.logo} />
        </View>

        {/* Primary Navigation */}
        <View style={styles.navSection}>
          {primaryNavItems.map((item) => {
            const isActive = activeRouteName === item.route;
            return (
              <TouchableOpacity
                key={item.route}
                style={[styles.navItem, isActive && styles.navItemActive]}
                onPress={() => navigateTo(item.route)}
                activeOpacity={0.7}
              >
                <View style={styles.navIconContainer}>
                  <Image
                    source={isActive ? item.activeIcon : item.icon}
                    style={[
                      styles.navIcon,
                      !isActive && { tintColor: Colors.GrayPallete.lightgrayThree },
                      isActive && item.label !== 'Home' && { tintColor: Colors.RedPallete.red },
                    ]}
                  />
                </View>
                <Text style={[styles.navLabel, isActive && styles.navLabelActive]}>
                  {item.label}
                </Text>
                {item.showBadge && totalItems > 0 && (
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>
                      {totalItems > 99 ? '99+' : totalItems}
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Menu Items */}
        <View style={styles.menuSection}>
          {menuItems.map((item) => (
            <TouchableOpacity
              key={item.label}
              style={styles.menuItem}
              onPress={item.onPress}
              activeOpacity={0.7}
            >
              <View style={styles.menuIconContainer}>
                <Image
                  source={item.icon}
                  style={[
                    styles.menuIcon,
                    { tintColor: Colors.GrayPallete.grayOne },
                  ]}
                />
              </View>
              <Text style={styles.menuLabel}>{item.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Secondary Items */}
        <View style={styles.secondarySection}>
          {secondaryItems.map((item) => (
            <TouchableOpacity
              key={item.label}
              style={styles.menuItem}
              onPress={item.onPress}
              activeOpacity={0.7}
            >
              <View style={styles.menuIconContainer}>
                <Image
                  source={item.icon}
                  style={[
                    styles.menuIcon,
                    { tintColor: Colors.GrayPallete.lightGrayFour },
                  ]}
                />
              </View>
              <Text style={[styles.menuLabel, styles.secondaryLabel]}>{item.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Client Info */}
        <View style={styles.clientSection}>
          <Text style={styles.clientName} numberOfLines={2}>
            {user?.current_client?.name}
          </Text>
          <Text selectable style={styles.clientInfo}>
            Cód. Brasal:{' '}
            <Text style={styles.clientInfoValue}>
              {user?.current_client?.clientCode || '******'}
            </Text>
          </Text>
          <Text selectable style={styles.clientInfo}>
            CPF/CNPJ:{' '}
            <Text style={styles.clientInfoValue}>
              {user?.current_client?.cnpj_cpf || '*****'}
            </Text>
          </Text>
          {storeAccess && limit != null && balance != null && (
            <Text selectable style={styles.clientInfo}>
              Saldo:{' '}
              <Text style={styles.clientInfoValue}>
                {balance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                {' / '}
                {limit.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </Text>
            </Text>
          )}
        </View>

        {/* Logout */}
        <TouchableOpacity style={styles.logoutButton} onPress={handleLogOff}>
          <View style={styles.menuIconContainer}>
            <Image
              source={Images.user}
              style={[styles.menuIcon, { tintColor: Colors.GrayPallete.lightGrayFour }]}
            />
          </View>
          <Text style={styles.logoutText}>Sair</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

export default DesktopSidebar;
